import type { TourDefinition } from "@/tours/types";

const LOAD_TIMEOUT_MS = 10000;

export type Registry = typeof import("@/tours/registry");
// Loaded when a tour starts, so pages where none runs don't download them all.
let registry: Registry | null = null;
let registryLoad: Promise<Registry> | null = null;

function loadRegistry(): Promise<Registry> {
  registryLoad ??= import("@/tours/registry").then(
    (loaded) => {
      registry = loaded;
      return loaded;
    },
    (error: unknown) => {
      registryLoad = null;
      throw error;
    },
  );
  return registryLoad;
}

// A dynamic import has no timeout of its own, and popups stay held while it
// loads: a stalled one counts as failed. A retry waits on the same import,
// which the browser wouldn't start again anyway.
export function inTime<T>(load: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error("Tour code took too long to load")),
      LOAD_TIMEOUT_MS,
    );
  });
  return Promise.race([load, timeout]).finally(() => clearTimeout(timer));
}

export function loadRegistryInTime(): Promise<Registry> {
  return inTime(loadRegistry());
}

export function registryIfLoaded(): Registry | null {
  return registry;
}

// Every run loads the registry before its first step.
export function loadedRegistry(): Registry {
  if (!registry) throw new Error("Tour registry used before it loaded");
  return registry;
}

export function registeredStep(tour: TourDefinition, index: number): number {
  const registered =
    registry?.TOURS[tour.key]?.steps.indexOf(tour.steps[index]) ?? -1;
  return registered === -1 ? index : registered;
}
